import type { InspectionTemplateBody } from '@ayumia/contracts';
import { text } from '../app/i18n';
import { evaluateFormCondition, resolveProperty } from '@ayumia/component-runtime';
import { duplicateKey, warningText, type FormWarning } from './form-warnings';
import { holds, type Answers, type Entry, type Section } from './form-targets';

/**
 * Registre d'une série (inspection-template-body 1.6.0, addendum d'ADR-141 du 2026-10-19) : la série
 * présentée en tableau dense (`series.presentation: ledger`), un panneau de saisie fixe et une
 * synthèse déclarée par agrégats (`series.ledger.summary`). Même calcul que la tablette
 * (`series_ledger.dart`), éprouvé par le jeu partagé `series-ledger.cases.json`. Présentation
 * seulement : le serveur relit chaque ligne contre le gabarit.
 */

type Localized = Readonly<Record<string, string>> | undefined;

export interface LedgerAggregate {
  kind: 'count' | 'sum' | 'count_by' | 'nonconform' | 'duplicates';
  field?: string;
  label?: Localized;
  unit?: string;
}

export interface LedgerSpec {
  columns?: string[];
  focus_field?: string;
  keep_fields?: string[];
  summary?: LedgerAggregate[];
}

export interface SummaryItem {
  kind: LedgerAggregate['kind'];
  label: string;
  value: number;
  unit?: string;
  by?: Array<{ value: string; label: string; count: number }>;
}

export interface FormStep {
  step_id: string;
  label: Localized;
  sections: string[];
}

type SeriesOf = Omit<NonNullable<Section['series']>, 'ledger' | 'presentation'> & { presentation?: string; ledger?: LedgerSpec };

export function seriesOf(section: Section): SeriesOf | undefined {
  return section.series as unknown as SeriesOf | undefined;
}

export function isLedger(section: Section): boolean {
  return seriesOf(section)?.presentation === 'ledger';
}

/** Étapes de saisie déclarées par le gabarit (1.6.0) ; une section citée nulle part va à la dernière. */
export function stepsOf(template: InspectionTemplateBody): FormStep[] {
  const declared = ((template as unknown as { steps?: FormStep[] }).steps ?? []).filter((step) => Array.isArray(step.sections) && step.sections.length > 0);
  if (declared.length === 0) return [];
  const cited = new Set(declared.flatMap((step) => step.sections));
  const rest = template.sections.map((section) => section.section_id).filter((id) => !cited.has(id));
  if (rest.length === 0) return declared;
  return declared.map((step, index) => (index === declared.length - 1 ? { ...step, sections: [...step.sections, ...rest] } : step));
}

/** Champs du tableau et du panneau, dans l'ordre déclaré (`ledger.columns`), sinon ceux de la série ; jamais un champ calculé. */
export function ledgerEntries(section: Section): Entry[] {
  const fields = section.fields.filter((entry) => entry.compute === undefined);
  const columns = seriesOf(section)?.ledger?.columns;
  if (columns === undefined || columns.length === 0) return fields;
  return columns.map((id) => fields.find((entry) => entry.field.field_id === id)).filter((entry): entry is Entry => entry !== undefined);
}

/**
 * Champs du panneau de saisie : ceux des colonnes, dans leur ordre, puis les champs saisissables sans colonne (la
 * précision du motif « Autre », ADR-147) : une colonne range le tableau, elle ne retire pas un champ de la saisie ;
 * `visible_when` décide seul de ce qui est proposé. Même règle que la tablette.
 */
export function ledgerPanelEntries(section: Section): Entry[] {
  const listed = ledgerEntries(section);
  const ids = new Set(listed.map((entry) => entry.field.field_id));
  return [...listed, ...section.fields.filter((entry) => entry.compute === undefined && !ids.has(entry.field.field_id))];
}

function enumLabel(entry: Entry | undefined, value: string, language: string): string {
  const labels = (entry?.field.validation as { enum_labels?: Record<string, Localized> } | undefined)?.enum_labels;
  return text(labels?.[value], language, value);
}

function enumValues(entry: Entry | undefined): string[] {
  return ((entry?.field.validation as { enum_values?: string[] } | undefined)?.enum_values ?? []).map(String);
}

function numeric(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value !== null && typeof value === 'object' && typeof (value as { value?: unknown }).value === 'number') return (value as { value: number }).value;
  return null;
}

/** Une ligne est non conforme quand un de ses champs déclare `nonconformity_when` et que la condition tient. */
export function rowNonconform(section: Section, row: Answers): boolean {
  return section.fields.some((entry) => entry.nonconformity_when !== undefined && holds(entry.nonconformity_when, row));
}

/** Valeurs d'un champ présentes sur plus d'une ligne. */
export function duplicatedValues(rows: readonly Answers[], field: string): Set<string> {
  const seen = new Map<string, number>();
  for (const row of rows) {
    const key = duplicateKey(row[field]);
    if (key !== null) seen.set(key, (seen.get(key) ?? 0) + 1);
  }
  return new Set([...seen].filter(([, count]) => count > 1).map(([key]) => key));
}

const DEFAULT_LABEL: Readonly<Record<LedgerAggregate['kind'], string>> = { count: 'Lignes', sum: 'Total', count_by: 'Par valeur', nonconform: 'Non conformes', duplicates: 'Doublons' };

/** Synthèse d'une série : chaque agrégat déclaré, sur les lignes telles qu'elles sont (sans la clé `exit`). */
export function ledgerSummary(section: Section, rows: readonly Answers[], language = 'fr'): SummaryItem[] {
  const spec = seriesOf(section)?.ledger;
  const clean = rows.map((row) => Object.fromEntries(Object.entries(row ?? {}).filter(([key]) => key !== 'exit')));
  const entryOf = (id: string | undefined): Entry | undefined => section.fields.find((entry) => entry.field.field_id === id);
  return (spec?.summary ?? []).map((aggregate): SummaryItem => {
    const entry = entryOf(aggregate.field);
    const fieldLabel = entry === undefined ? undefined : text(entry.field.label, language, aggregate.field ?? '');
    const label = aggregate.label === undefined ? (fieldLabel ?? DEFAULT_LABEL[aggregate.kind]) : text(aggregate.label, language, DEFAULT_LABEL[aggregate.kind]);
    switch (aggregate.kind) {
      case 'count':
        return { kind: 'count', label, value: clean.length };
      case 'sum': {
        const total = clean.reduce((sum, row) => sum + (numeric(row[aggregate.field ?? '']) ?? 0), 0);
        const unit = aggregate.unit ?? (entry?.field as { unit?: string } | undefined)?.unit;
        return { kind: 'sum', label, value: Math.round(total * 1000) / 1000, ...(unit === undefined ? {} : { unit }) };
      }
      case 'count_by': {
        const counts = new Map<string, number>();
        for (const row of clean) {
          const key = duplicateKey(row[aggregate.field ?? '']);
          if (key !== null) counts.set(key, (counts.get(key) ?? 0) + 1);
        }
        const order = [...enumValues(entry).filter((value) => counts.has(value)), ...[...counts.keys()].filter((value) => !enumValues(entry).includes(value))];
        return { kind: 'count_by', label, value: clean.length, by: order.map((value) => ({ value, label: enumLabel(entry, value, language), count: counts.get(value)! })) };
      }
      case 'nonconform':
        return { kind: 'nonconform', label, value: clean.filter((row) => rowNonconform(section, row)).length };
      case 'duplicates':
        return { kind: 'duplicates', label, value: duplicatedValues(clean, aggregate.field ?? '').size };
    }
  });
}

/**
 * Avertissements de la ligne en saisie, avant son ajout : la valeur déjà présente sur une autre ligne
 * (`duplicates.in_series`) et les avertissements déclarés du champ (`warnings[].when` sur `$answers`
 * de la ligne et `$form` de la fiche) — les mêmes que ceux de la fiche une fois la ligne ajoutée.
 */
export function draftWarnings(section: Section, draft: Answers, rows: readonly Answers[], editing: number | null, form: Answers): Array<{ field: string; message: string }> {
  const out: Array<{ field: string; message: string }> = [];
  for (const entry of section.fields) {
    const id = entry.field.field_id;
    const duplicates = (entry as { duplicates?: { in_series?: boolean; in_series_message?: Localized } }).duplicates;
    const key = duplicateKey(draft[id]);
    if (duplicates?.in_series === true && key !== null) {
      const other = rows.findIndex((row, index) => index !== editing && duplicateKey(row?.[id]) === key);
      if (other >= 0) out.push({ field: id, message: warningText(duplicates.in_series_message, 'Valeur {value} déjà saisie ligne {line}', { value: key, line: other + 1 }) });
    }
    for (const declared of (entry as { warnings?: Array<{ warning_id: string; when: unknown; message?: Localized }> }).warnings ?? []) {
      let holdsNow = false;
      try {
        const value = resolveProperty({ expression: declared.when }, { answers: draft, form });
        holdsNow = !(value === false || value === null || value === undefined || value === 0);
      } catch {
        holdsNow = false;
      }
      if (holdsNow) out.push({ field: id, message: warningText(declared.message, declared.warning_id, { value: key }) });
    }
  }
  return out;
}

/**
 * Ligne suivante : la valeur par défaut des champs (`field.default`), puis les champs repris
 * (`keep_fields`) de la ligne ajoutée — l'espèce reste choisie, « Conforme » d'office.
 */
export function nextDraft(section: Section, added: Answers = {}): Answers {
  const draft: Answers = {};
  for (const entry of section.fields) {
    const fallback = (entry.field as { default?: unknown }).default;
    if (fallback !== undefined && fallback !== null && entry.compute === undefined) draft[entry.field.field_id] = fallback;
  }
  for (const id of seriesOf(section)?.ledger?.keep_fields ?? []) if (added[id] !== undefined && added[id] !== null) draft[id] = added[id];
  return draft;
}

/** Champ où revient le curseur après un ajout : `focus_field`, sinon le premier champ saisi au pavé (`keyable`). */
export function focusFieldOf(section: Section, keyable: (entry: Entry) => boolean): string | undefined {
  const declared = seriesOf(section)?.ledger?.focus_field;
  if (declared !== undefined) return declared;
  return ledgerEntries(section).find(keyable)?.field.field_id;
}

/**
 * Champs obligatoires de la ligne en saisie encore vides (`field.required` ou `required_when`, champs
 * visibles du tableau), dans l'ordre des colonnes : tant qu'il en reste, la ligne ne s'ajoute pas et le
 * bouton y mène. Doublon et motif manquant restent des avertissements. Même calcul que la tablette.
 */
export function draftMissing(section: Section, draft: Answers): string[] {
  return ledgerEntries(section)
    .filter((entry) => evaluateFormCondition(entry.visible_when, draft))
    .filter((entry) => entry.field.required === true || (entry.required_when !== undefined && evaluateFormCondition(entry.required_when, draft)))
    .filter((entry) => draft[entry.field.field_id] === undefined || draft[entry.field.field_id] === null || draft[entry.field.field_id] === '')
    .map((entry) => entry.field.field_id);
}

/** Un doublon de la synthèse : la valeur, son champ et toutes ses lignes (numérotées à partir de 1). */
export interface DuplicateGroup {
  path: string;
  field: string;
  label: string;
  value: string;
  lines: number[];
}

/**
 * Doublons dans une série regroupés par valeur : une carte « Bague 102 : lignes 02 et 08 » au lieu
 * d'une par ligne (02 ↔ 08, 08 ↔ 02). `path` mène à la première ligne. Même calcul que la tablette.
 */
export function duplicateGroups(sections: readonly Section[], answers: Answers, warnings: readonly FormWarning[], language = 'fr'): DuplicateGroup[] {
  const groups = new Map<string, DuplicateGroup>();
  for (const warning of warnings) {
    if (warning.code !== 'FORM_DUPLICATE_IN_SERIES') continue;
    const parts = warning.field.split('.');
    if (parts.length !== 4) continue;
    const [, sectionId, rawIndex, fieldId] = parts as [string, string, string, string];
    const index = Number(rawIndex);
    const rows = answers[sectionId];
    if (!Number.isInteger(index) || !Array.isArray(rows) || index >= rows.length) continue;
    const value = duplicateKey((rows[index] as Answers)[fieldId]);
    if (value === undefined || value === null) continue;
    const entry = sections.find((section) => section.section_id === sectionId)?.fields.find((candidate) => candidate.field.field_id === fieldId);
    const key = `${sectionId}.${fieldId}.${value}`;
    const group = groups.get(key) ?? { path: warning.field, field: fieldId, label: entry === undefined ? fieldId : text(entry.field.label, language, fieldId), value, lines: [] };
    for (const line of [index + 1, ...(warning.line === undefined ? [] : [warning.line])]) if (!group.lines.includes(line)) group.lines.push(line);
    groups.set(key, group);
  }
  return [...groups.values()].map((group) => ({ ...group, lines: [...group.lines].sort((a, b) => a - b) }));
}

/**
 * Quitter la ligne en saisie pour une autre (« ‹ » ligne précédente, « › » ligne suivante, comme « Carcasse
 * suivante » de l'écran v1) : `move` (rien n'a changé), `save` (changement complet : la ligne est enregistrée
 * avant de partir), `stay` (changement incomplet : on reste, le champ qui manque est montré). `draft` est la
 * ligne telle qu'elle partirait (nombres lus, vides retirés). Même règle que la tablette, jeu partagé
 * `ledger-navigation.cases.json`.
 */
export function ledgerLeave(section: Section, rows: readonly Answers[], editing: number | null, draft: Answers): 'move' | 'save' | 'stay' {
  const clean = (row: Answers): Record<string, unknown> => Object.fromEntries(Object.entries(row ?? {}).filter(([key, value]) => value !== null && value !== undefined && value !== '' && key !== 'exit'));
  const base = clean(editing !== null && editing < rows.length ? rows[editing]! : nextDraft(section, rows.at(-1) ?? {}));
  const now = clean(draft);
  const same = Object.keys(base).length === Object.keys(now).length && Object.entries(base).every(([key, value]) => key in now && String(now[key]) === String(value));
  if (same) return 'move';
  return draftMissing(section, now).length === 0 ? 'save' : 'stay';
}

/** Position de la ligne en saisie : « Ligne 3 / 7 » en correction, « Ligne 8 / 8 » pour une nouvelle ligne. */
export function ledgerPosition(rows: number, editing: number | null): { current: number; total: number } {
  return editing !== null ? { current: editing + 1, total: rows } : { current: rows + 1, total: rows + 1 };
}
